'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useUserWorkspace } from '@kit/accounts/hooks/use-user-workspace';
import { getSupabaseBrowserClient } from '@kit/supabase/browser-client';
import { useAuthChangeListener } from '@kit/supabase/hooks/use-auth-change-listener';

import pathsConfig from '~/config/paths.config';

const CreditContext = createContext<{
  credits: number | null;
  refetchCredits: () => void;
  triggerTemporaryRefetchLoop: () => void;
}>({
  credits: null,
  refetchCredits: () => {},
  triggerTemporaryRefetchLoop: () => {},
});

export function CreditProvider({ children }: { children: React.ReactNode }) {
  const client = getSupabaseBrowserClient();
  const [accountId, setAccountId] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Ref to prevent overlapping loops
  const refetchLoopActive = useRef(false);

  // redirectOnSignOut: false -> this second mount only tracks auth state for the
  // credits query; the AuthProvider mount owns the sign-out redirect/reload so
  // it does not run twice per event.
  useAuthChangeListener({
    appHomePath: pathsConfig.app.home,
    redirectOnSignOut: false,
    onEvent: (event, session) => {
      if (session?.user?.id) {
        setAccountId(session.user.id);
        setIsAuthenticated(true);
      } else {
        setAccountId(null);
        setIsAuthenticated(false);
      }
    },
  });

  const { data: credits, refetch } = useQuery({
    queryKey: ['current_credits', accountId],
    queryFn: async () => {
      if (!accountId) return null;
      const { data, error } = await client.rpc('nrf_get_account_credits', {
        account_id: accountId,
      });
      if (error) throw error;
      return data;
    },
    enabled: !!accountId,
  });

  // Trigger a 20s refetch loop every 2s
  const triggerTemporaryRefetchLoop = () => {
    if (refetchLoopActive.current) return;

    refetchLoopActive.current = true;
    const interval = setInterval(() => {
      refetch();
    }, 2000);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      refetchLoopActive.current = false;
    }, 20000);
  };

  return (
    <CreditContext.Provider
      value={{
        credits: credits ?? null,
        refetchCredits: refetch,
        triggerTemporaryRefetchLoop,
      }}
    >
      {children}
    </CreditContext.Provider>
  );
}

export function useCredits() {
  return useContext(CreditContext);
}
